import { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  FileText,
  ChevronDown,
  ChevronUp,
  Search,
  Plus,
  Trash2,
  Loader2,
  User,
  Package,
  Truck,
  MapPin,
  Calculator,
  X,
  Check,
} from 'lucide-react'
import {
  manualInvoices,
  ManualInvoiceCustomer,
  ManualInvoiceProduct,
  ShippingOption,
  PaymentMethod,
} from '@/lib/api'
import { useDebounce } from '@/hooks/useDebounce'
import { useToast } from '@/components/ui/Toast'

// ============================================================================
// Types
// ============================================================================

interface InvoiceItem {
  reference: string
  name: string
  quantity: number
  price_net: number
  cost: number
  iva_type: number
  weight: number
  is_manual?: boolean
}

interface CustomAddress {
  name: string
  street: string
  city: string
  postal_code: string
  province: string
  country: string
  phone: string
  email: string
}

// ============================================================================
// Constants
// ============================================================================

const IVA_RATES: Record<number, number> = {
  0: 21,
  1: 10,
  2: 4,
  3: 0,
}

const IVA_LABELS: Record<number, string> = {
  0: '21%',
  1: '10%',
  2: '4%',
  3: '0% (Exento)',
}

const COUNTRIES = [
  { code: 'ES', name: 'Espana' },
  { code: 'PT', name: 'Portugal' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgica' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croacia' },
  { code: 'CY', name: 'Chipre' },
  { code: 'CZ', name: 'Chequia' },
  { code: 'DK', name: 'Dinamarca' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'FR', name: 'Francia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'GR', name: 'Grecia' },
  { code: 'HU', name: 'Hungria' },
  { code: 'IE', name: 'Irlanda' },
  { code: 'IT', name: 'Italia' },
  { code: 'LV', name: 'Letonia' },
  { code: 'LT', name: 'Lituania' },
  { code: 'LU', name: 'Luxemburgo' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Paises Bajos' },
  { code: 'PL', name: 'Polonia' },
  { code: 'RO', name: 'Rumania' },
  { code: 'SK', name: 'Eslovaquia' },
  { code: 'SI', name: 'Eslovenia' },
  { code: 'SE', name: 'Suecia' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'CH', name: 'Suiza' },
  { code: 'NO', name: 'Noruega' },
  { code: 'AD', name: 'Andorra' },
]

const EMPTY_ADDRESS: CustomAddress = {
  name: '',
  street: '',
  city: '',
  postal_code: '',
  province: '',
  country: 'ES',
  phone: '',
  email: '',
}

// ============================================================================
// Collapsible Section Component
// ============================================================================

function Section({
  title,
  icon: Icon,
  isOpen,
  onToggle,
  badge,
  children,
}: {
  title: string
  icon: any
  isOpen: boolean
  onToggle: () => void
  badge?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <span className="text-lg font-semibold text-slate-800 dark:text-slate-200">{title}</span>
          {badge}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && <div className="px-5 pb-5 border-t border-slate-100 dark:border-slate-700 pt-4">{children}</div>}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export default function ManualInvoicePage() {
  const toast = useToast()

  // Section visibility
  const [sections, setSections] = useState({
    customer: true,
    invoiceData: true,
    products: true,
    transport: true,
    address: true,
    summary: true,
  })

  const toggleSection = (key: keyof typeof sections) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // Customer state
  const [customerSearch, setCustomerSearch] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState<ManualInvoiceCustomer | null>(null)
  const [customerResults, setCustomerResults] = useState<ManualInvoiceCustomer[]>([])
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false)
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const customerDropdownRef = useRef<HTMLDivElement>(null)
  const debouncedCustomerSearch = useDebounce(customerSearch, 300)

  // Invoice data state
  const [invoiceReference, setInvoiceReference] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const warehouse = 'GEN'

  // Products state
  const [items, setItems] = useState<InvoiceItem[]>([])
  const [productSearch, setProductSearch] = useState('')
  const [productResults, setProductResults] = useState<ManualInvoiceProduct[]>([])
  const [isSearchingProducts, setIsSearchingProducts] = useState(false)
  const [showProductDropdown, setShowProductDropdown] = useState(false)
  const productDropdownRef = useRef<HTMLDivElement>(null)
  const debouncedProductSearch = useDebounce(productSearch, 300)

  // Transport state
  const [noShipping, setNoShipping] = useState(false)
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([])
  const [selectedShipping, setSelectedShipping] = useState<ShippingOption | null>(null)
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false)

  // Address state
  const [useCustomerAddress, setUseCustomerAddress] = useState(true)
  const [customAddress, setCustomAddress] = useState<CustomAddress>({ ...EMPTY_ADDRESS })

  // Queries
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['manual-invoice-payment-methods'],
    queryFn: manualInvoices.getPaymentMethods,
    staleTime: 1000 * 60 * 60,
  })

  const { data: nextNumber } = useQuery({
    queryKey: ['manual-invoice-next-number'],
    queryFn: manualInvoices.getNextNumber,
    staleTime: 1000 * 60 * 5,
  })

  // Set default reference when next number loads
  useEffect(() => {
    if (nextNumber && !invoiceReference) {
      setInvoiceReference(nextNumber.reference)
    }
  }, [nextNumber])

  // Customer search effect
  useEffect(() => {
    if (debouncedCustomerSearch.length < 2) {
      setCustomerResults([])
      setShowCustomerDropdown(false)
      return
    }

    let cancelled = false
    const search = async () => {
      setIsSearchingCustomers(true)
      try {
        const results = await manualInvoices.searchCustomers(debouncedCustomerSearch)
        if (!cancelled) {
          setCustomerResults(results)
          setShowCustomerDropdown(true)
        }
      } catch {
        if (!cancelled) setCustomerResults([])
      } finally {
        if (!cancelled) setIsSearchingCustomers(false)
      }
    }
    search()
    return () => { cancelled = true }
  }, [debouncedCustomerSearch])

  // Product search effect
  useEffect(() => {
    if (debouncedProductSearch.length < 2) {
      setProductResults([])
      setShowProductDropdown(false)
      return
    }

    let cancelled = false
    const tariff = selectedCustomer?.tariff || 1
    const search = async () => {
      setIsSearchingProducts(true)
      try {
        const results = await manualInvoices.searchProducts(debouncedProductSearch, tariff)
        if (!cancelled) {
          setProductResults(results)
          setShowProductDropdown(true)
        }
      } catch {
        if (!cancelled) setProductResults([])
      } finally {
        if (!cancelled) setIsSearchingProducts(false)
      }
    }
    search()
    return () => { cancelled = true }
  }, [debouncedProductSearch, selectedCustomer?.tariff])

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false)
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node)) {
        setShowProductDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Determine if intracom from customer's Factusol config (TIVCLI=4)
  const isIntracom = selectedCustomer?.is_intracom ?? false

  // Get effective IVA rate for a type
  const getIvaRate = (ivaType: number): number => {
    if (isIntracom) return 0
    return IVA_RATES[ivaType] ?? 21
  }

  // Calculate totals
  const totals = useMemo(() => {
    const byIva: Record<number, { base: number; rate: number }> = {}

    for (const item of items) {
      const rate = getIvaRate(item.iva_type)
      const lineTotal = item.quantity * item.price_net
      if (!byIva[rate]) byIva[rate] = { base: 0, rate }
      byIva[rate].base += lineTotal
    }

    const subtotal = Object.values(byIva).reduce((sum, g) => sum + g.base, 0)
    const totalIva = Object.values(byIva).reduce((sum, g) => sum + g.base * (g.rate / 100), 0)

    const shippingPrice = noShipping ? 0 : (selectedShipping?.price_net || 0)
    const shippingIvaRate = isIntracom ? 0 : 21
    const shippingIva = shippingPrice * (shippingIvaRate / 100)

    const grandTotal = subtotal + totalIva + shippingPrice + shippingIva

    return { byIva, subtotal, totalIva, shippingPrice, shippingIvaRate, shippingIva, grandTotal }
  }, [items, selectedShipping, noShipping, isIntracom])

  // Total weight
  const totalWeight = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.weight || 0.1) * item.quantity, 0)
  }, [items])

  // Is COD
  const isCod = paymentMethod === '002'

  // Select customer
  const handleSelectCustomer = (customer: ManualInvoiceCustomer) => {
    setSelectedCustomer(customer)
    setCustomerSearch('')
    setShowCustomerDropdown(false)

    // Set payment method from customer if available
    if (customer.payment_method) {
      setPaymentMethod(customer.payment_method)
    }

    // If customer address changes and customer tariff changes, product prices may change
    // but we leave existing items as-is (user chose them)
  }

  // Clear customer
  const handleClearCustomer = () => {
    setSelectedCustomer(null)
    setCustomerSearch('')
    setCustomerResults([])
  }

  // Add product to items
  const handleAddProduct = (product: ManualInvoiceProduct) => {
    // Check if already in list
    const existing = items.findIndex((i) => i.reference === product.reference)
    if (existing >= 0) {
      // Increase quantity
      const updated = [...items]
      updated[existing].quantity += 1
      setItems(updated)
    } else {
      setItems([
        ...items,
        {
          reference: product.reference,
          name: product.name,
          quantity: 1,
          price_net: product.price_net,
          cost: product.cost,
          iva_type: product.iva_type,
          weight: product.weight || 0.1,
        },
      ])
    }
    setProductSearch('')
    setShowProductDropdown(false)
  }

  // Add manual (free-form) product
  const handleAddManualProduct = () => {
    setItems([
      ...items,
      {
        reference: '',
        name: '',
        quantity: 1,
        price_net: 0,
        cost: 0,
        iva_type: 0,
        weight: 0.5,
        is_manual: true,
      },
    ])
  }

  // Update item field
  const updateItem = (index: number, field: keyof InvoiceItem, value: number | string) => {
    const updated = [...items]
    ;(updated[index] as any)[field] = value
    setItems(updated)
  }

  // Remove item
  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  // Calculate shipping
  const handleCalculateShipping = async () => {
    const postalCode = useCustomerAddress
      ? selectedCustomer?.postal_code || ''
      : customAddress.postal_code
    const country = useCustomerAddress
      ? selectedCustomer?.country || 'ES'
      : customAddress.country

    if (!postalCode) {
      toast.warning('Se necesita un codigo postal para calcular el transporte')
      return
    }

    setIsCalculatingShipping(true)
    try {
      const options = await manualInvoices.calculateShipping({
        postal_code: postalCode,
        country,
        weight: Math.max(totalWeight, 0.1),
      })
      setShippingOptions(options)
      // Auto-select best available option (first same-day, or cheapest available)
      if (options.length > 0) {
        const availableOptions = options.filter((o) => o.available)
        const sameDay = availableOptions.find((o) => o.same_day_available)
        setSelectedShipping(sameDay || availableOptions[0] || options[0])
      }
    } catch (err: any) {
      toast.error('Error calculando transporte', err?.response?.data?.detail || err.message)
    } finally {
      setIsCalculatingShipping(false)
    }
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: manualInvoices.create,
    onSuccess: (data: any) => {
      toast.success(
        `Factura ${data.invoice_reference || invoiceReference} creada`,
        `Pedido ${data.manual_order_reference || ''} enviado al centro de envios.`
      )
      // Reset form
      resetForm()
    },
    onError: (err: any) => {
      toast.error('Error creando factura', err?.response?.data?.detail || err.message)
    },
  })

  const resetForm = () => {
    setSelectedCustomer(null)
    setCustomerSearch('')
    setInvoiceReference('')
    setPaymentMethod('')
    setItems([])
    setProductSearch('')
    setNoShipping(false)
    setShippingOptions([])
    setSelectedShipping(null)
    setUseCustomerAddress(true)
    setCustomAddress({ ...EMPTY_ADDRESS })
  }

  // Validate and submit
  const handleSubmit = () => {
    if (!selectedCustomer) {
      toast.warning('Selecciona un cliente')
      return
    }
    if (!paymentMethod) {
      toast.warning('Selecciona una forma de pago')
      return
    }
    if (items.length === 0) {
      toast.warning('Anade al menos un producto')
      return
    }
    const invalidManual = items.find((i) => i.is_manual && (!i.reference.trim() || !i.name.trim()))
    if (invalidManual) {
      toast.warning('Los productos manuales necesitan referencia y descripcion')
      return
    }

    const address = useCustomerAddress
      ? {
          name: selectedCustomer.company || selectedCustomer.name,
          street: selectedCustomer.address,
          city: selectedCustomer.city,
          postal_code: selectedCustomer.postal_code,
          province: selectedCustomer.province || '',
          country: selectedCustomer.country,
          phone: selectedCustomer.phone || '',
          email: selectedCustomer.email || '',
        }
      : customAddress

    createMutation.mutate({
      customer_code: selectedCustomer.code,
      reference: invoiceReference,
      payment_method: paymentMethod,
      warehouse,
      items: items.map((i) => ({
        reference: i.reference,
        description: i.name,
        quantity: i.quantity,
        price_net: i.price_net,
        iva_type: i.iva_type,
        weight: i.weight,
        cost: i.cost,
      })),
      shipping_cost_net: noShipping || !selectedShipping ? 0 : selectedShipping.price_net,
      shipping_iva_type: isIntracom ? 3 : 0,
      carrier_code: noShipping || !selectedShipping ? undefined : selectedShipping.carrier_name,
      custom_shipping_address: address ? {
        name: address.name,
        street: address.street,
        city: address.city,
        postal_code: address.postal_code,
        province: address.province,
        country: address.country,
        phone: address.phone,
        email: address.email,
      } : undefined,
    })
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/40">
            <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Nueva Factura</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Crear factura manual en Factusol</p>
          </div>
        </div>
        {nextNumber && (
          <div className="text-right">
            <span className="text-sm text-slate-500 dark:text-slate-400">Prox. numero (aprox.):</span>
            <span className="ml-2 font-mono font-semibold text-slate-800 dark:text-slate-200">
              ~{nextNumber.number}
            </span>
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* CLIENTE */}
      {/* ================================================================ */}
      <Section
        title="Cliente"
        icon={User}
        isOpen={sections.customer}
        onToggle={() => toggleSection('customer')}
        badge={
          selectedCustomer ? (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
              {selectedCustomer.code}
            </span>
          ) : undefined
        }
      >
        {!selectedCustomer ? (
          <div className="relative" ref={customerDropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Buscar por nombre, NIF, email..."
                className="w-full pl-10 pr-10 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
              {isSearchingCustomers && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
              )}
            </div>

            {showCustomerDropdown && customerResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {customerResults.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleSelectCustomer(c)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-slate-900 dark:text-white">{c.name}</span>
                        {c.company && (
                          <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">({c.company})</span>
                        )}
                      </div>
                      <span className="text-xs font-mono text-slate-400">{c.code}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {c.nif && <span>{c.nif}</span>}
                      {c.email && <span>{c.email}</span>}
                      <span className="ml-auto px-1.5 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        Tarifa {c.tariff}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showCustomerDropdown && debouncedCustomerSearch.length >= 2 && customerResults.length === 0 && !isSearchingCustomers && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-4 text-sm text-slate-500 dark:text-slate-400">
                No se encontraron clientes
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900 dark:text-white text-lg">
                    {selectedCustomer.name}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-mono rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    {selectedCustomer.code}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                    Tarifa {selectedCustomer.tariff}
                  </span>
                </div>
                {selectedCustomer.company && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedCustomer.company}</p>
                )}
                <div className="flex items-center gap-4 mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {selectedCustomer.nif && <span>NIF: {selectedCustomer.nif}</span>}
                  {selectedCustomer.email && <span>{selectedCustomer.email}</span>}
                  {selectedCustomer.phone && <span>{selectedCustomer.phone}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={handleClearCustomer}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                title="Cambiar cliente"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg text-sm text-slate-600 dark:text-slate-400">
              <MapPin className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              {selectedCustomer.address}, {selectedCustomer.postal_code} {selectedCustomer.city}
              {selectedCustomer.province && `, ${selectedCustomer.province}`} - {selectedCustomer.country}
            </div>
          </div>
        )}
      </Section>

      {/* ================================================================ */}
      {/* DATOS FACTURA */}
      {/* ================================================================ */}
      <Section
        title="Datos Factura"
        icon={FileText}
        isOpen={sections.invoiceData}
        onToggle={() => toggleSection('invoiceData')}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Referencia <span className="text-xs font-normal text-slate-400">(opcional, se auto-genera)</span>
            </label>
            <input
              type="text"
              value={invoiceReference}
              onChange={(e) => setInvoiceReference(e.target.value)}
              placeholder="Se asigna automaticamente"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Forma de pago <span className="text-red-500">*</span>
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            >
              <option value="">Seleccionar...</option>
              {paymentMethods.map((pm: PaymentMethod) => (
                <option key={pm.code} value={pm.code}>
                  {pm.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* PRODUCTOS */}
      {/* ================================================================ */}
      <Section
        title="Productos"
        icon={Package}
        isOpen={sections.products}
        onToggle={() => toggleSection('products')}
        badge={
          items.length > 0 ? (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
              {items.length}
            </span>
          ) : undefined
        }
      >
        {/* Product search + manual add */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1" ref={productDropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Buscar producto por referencia o nombre..."
                className="w-full pl-10 pr-10 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
              {isSearchingProducts && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
              )}
            </div>

            {showProductDropdown && productResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {productResults.map((p) => (
                  <button
                    key={p.reference}
                    type="button"
                    onClick={() => handleAddProduct(p)}
                    className="w-full px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <span className="font-mono text-xs text-slate-500 dark:text-slate-400 mr-2">{p.reference}</span>
                        <span className="text-sm text-slate-900 dark:text-white">{p.name}</span>
                      </div>
                      <div className="flex items-center gap-3 ml-3 shrink-0">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">
                          {p.price_net.toFixed(2)} EUR
                        </span>
                        <span className={`text-xs ${p.stock > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                          Stock: {p.stock}
                        </span>
                        <Plus className="w-4 h-4 text-primary-500" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showProductDropdown && debouncedProductSearch.length >= 2 && productResults.length === 0 && !isSearchingProducts && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-4 text-sm text-slate-500 dark:text-slate-400">
                No se encontraron productos
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleAddManualProduct}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors whitespace-nowrap"
            title="Anadir producto no registrado en Factusol"
          >
            <Plus className="w-4 h-4" />
            Manual
          </button>
        </div>

        {/* Items table */}
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-2 px-2 text-slate-500 dark:text-slate-400 font-medium">Ref</th>
                  <th className="text-left py-2 px-2 text-slate-500 dark:text-slate-400 font-medium">Descripcion</th>
                  <th className="text-center py-2 px-2 text-slate-500 dark:text-slate-400 font-medium w-16">Cant</th>
                  <th className="text-right py-2 px-2 text-slate-500 dark:text-slate-400 font-medium w-24">Precio</th>
                  <th className="text-right py-2 px-2 text-slate-500 dark:text-slate-400 font-medium w-24">Coste</th>
                  <th className="text-center py-2 px-2 text-slate-500 dark:text-slate-400 font-medium w-20">IVA</th>
                  <th className="text-right py-2 px-2 text-slate-500 dark:text-slate-400 font-medium w-20">Peso</th>
                  <th className="text-right py-2 px-2 text-slate-500 dark:text-slate-400 font-medium w-24">Total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {items.map((item, idx) => (
                  <tr key={`item-${idx}`} className="group">
                    <td className="py-2 px-2">
                      {item.is_manual ? (
                        <input
                          type="text"
                          value={item.reference}
                          onChange={(e) => updateItem(idx, 'reference', e.target.value)}
                          placeholder="REF"
                          className="w-28 px-1.5 py-1 text-xs font-mono border border-amber-300 dark:border-amber-600 rounded bg-amber-50 dark:bg-amber-900/20 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      ) : (
                        <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{item.reference}</span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      {item.is_manual ? (
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateItem(idx, 'name', e.target.value)}
                          placeholder="Descripcion del producto"
                          className="w-full px-1.5 py-1 text-sm border border-amber-300 dark:border-amber-600 rounded bg-amber-50 dark:bg-amber-900/20 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      ) : (
                        <span className="text-slate-900 dark:text-white max-w-xs truncate block" title={item.name}>
                          {item.name}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-16 mx-auto block text-center px-1 py-1 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price_net}
                        onChange={(e) => updateItem(idx, 'price_net', parseFloat(e.target.value) || 0)}
                        className="w-24 ml-auto block text-right px-1 py-1 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-2 px-2">
                      {item.is_manual ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.cost}
                          onChange={(e) => updateItem(idx, 'cost', parseFloat(e.target.value) || 0)}
                          className="w-20 ml-auto block text-right px-1 py-1 border border-amber-300 dark:border-amber-600 rounded bg-amber-50 dark:bg-amber-900/20 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      ) : (
                        <span className="block text-right text-xs text-slate-400 dark:text-slate-500">
                          {item.cost > 0 ? `${item.cost.toFixed(2)}` : '—'}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {item.is_manual ? (
                        <select
                          value={item.iva_type}
                          onChange={(e) => updateItem(idx, 'iva_type', parseInt(e.target.value))}
                          className="w-20 px-1 py-1 text-xs border border-amber-300 dark:border-amber-600 rounded bg-amber-50 dark:bg-amber-900/20 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          {Object.entries(IVA_LABELS).map(([val, label]) => (
                            <option key={val} value={val}>{label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {isIntracom ? '0%' : IVA_LABELS[item.iva_type]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      {item.is_manual ? (
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={item.weight}
                          onChange={(e) => updateItem(idx, 'weight', Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                          className="w-16 ml-auto block text-right px-1 py-1 border border-amber-300 dark:border-amber-600 rounded bg-amber-50 dark:bg-amber-900/20 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      ) : (
                        <span className="block text-right text-xs text-slate-400 dark:text-slate-500">
                          {item.weight.toFixed(1)} kg
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right font-medium text-slate-900 dark:text-white">
                      {(item.quantity * item.price_net).toFixed(2)} EUR
                    </td>
                    <td className="py-2 px-2">
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <span className="text-sm text-slate-500 dark:text-slate-400 mr-3">Subtotal:</span>
              <span className="font-semibold text-slate-900 dark:text-white">{totals.subtotal.toFixed(2)} EUR</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400 dark:text-slate-500">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Busca y anade productos a la factura</p>
          </div>
        )}
      </Section>

      {/* ================================================================ */}
      {/* TRANSPORTE */}
      {/* ================================================================ */}
      <Section
        title="Transporte"
        icon={Truck}
        isOpen={sections.transport}
        onToggle={() => toggleSection('transport')}
        badge={
          selectedShipping && !noShipping ? (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
              {selectedShipping.carrier_name} - {selectedShipping.price_net.toFixed(2)} EUR
            </span>
          ) : noShipping ? (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
              Sin transporte
            </span>
          ) : undefined
        }
      >
        <div className="space-y-4">
          {/* No shipping checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={noShipping}
              onChange={(e) => {
                setNoShipping(e.target.checked)
                if (e.target.checked) {
                  setSelectedShipping(null)
                  setShippingOptions([])
                }
              }}
              className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">Sin transporte</span>
          </label>

          {!noShipping && (
            <>
              {/* Calculate button */}
              <button
                type="button"
                onClick={handleCalculateShipping}
                disabled={isCalculatingShipping || items.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCalculatingShipping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Calculator className="w-4 h-4" />
                )}
                Calcular optimo
              </button>

              {/* Shipping options */}
              {shippingOptions.length > 0 && (
                <div className="space-y-2">
                  {shippingOptions.map((option) => (
                    <label
                      key={option.carrier_id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        !option.available
                          ? 'border-slate-200 dark:border-slate-700 opacity-50'
                          : selectedShipping?.carrier_id === option.carrier_id
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-500'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shipping"
                          checked={selectedShipping?.carrier_id === option.carrier_id}
                          onChange={() => setSelectedShipping(option)}
                          className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        />
                        <div>
                          <span className={`font-medium ${option.available ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`}>
                            {option.carrier_name}
                          </span>
                          {option.same_day_available && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              Same-day
                            </span>
                          )}
                          {!option.available && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                              Inactivo{option.inactive_until ? ` hasta ${option.inactive_until}` : ''}
                            </span>
                          )}
                          {option.available && option.zone_name && (
                            <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                              {option.zone_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="font-semibold text-slate-900 dark:text-white">{option.price_net.toFixed(2)} EUR</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Section>

      {/* ================================================================ */}
      {/* DIRECCION DE ENVIO */}
      {/* ================================================================ */}
      <Section
        title="Direccion de envio"
        icon={MapPin}
        isOpen={sections.address}
        onToggle={() => toggleSection('address')}
      >
        <div className="space-y-4">
          {/* Radio: use customer address */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="addressType"
              checked={useCustomerAddress}
              onChange={() => setUseCustomerAddress(true)}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">Usar direccion del cliente</span>
          </label>

          {useCustomerAddress && selectedCustomer && (
            <div className="ml-6 p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg text-sm text-slate-600 dark:text-slate-400">
              <p className="font-medium text-slate-800 dark:text-slate-200">{selectedCustomer.company || selectedCustomer.name}</p>
              <p>{selectedCustomer.address}</p>
              <p>
                {selectedCustomer.postal_code} {selectedCustomer.city}
                {selectedCustomer.province && `, ${selectedCustomer.province}`}
              </p>
              <p>{COUNTRIES.find((c) => c.code === selectedCustomer.country)?.name || selectedCustomer.country}</p>
              {selectedCustomer.phone && <p>Tel: {selectedCustomer.phone}</p>}
            </div>
          )}

          {/* Radio: custom address */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="addressType"
              checked={!useCustomerAddress}
              onChange={() => setUseCustomerAddress(false)}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">Direccion personalizada</span>
          </label>

          {!useCustomerAddress && (
            <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Nombre / Empresa
                </label>
                <input
                  type="text"
                  value={customAddress.name}
                  onChange={(e) => setCustomAddress({ ...customAddress, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Direccion
                </label>
                <input
                  type="text"
                  value={customAddress.street}
                  onChange={(e) => setCustomAddress({ ...customAddress, street: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Ciudad
                </label>
                <input
                  type="text"
                  value={customAddress.city}
                  onChange={(e) => setCustomAddress({ ...customAddress, city: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Codigo postal
                </label>
                <input
                  type="text"
                  value={customAddress.postal_code}
                  onChange={(e) => setCustomAddress({ ...customAddress, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Provincia
                </label>
                <input
                  type="text"
                  value={customAddress.province}
                  onChange={(e) => setCustomAddress({ ...customAddress, province: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Pais
                </label>
                <select
                  value={customAddress.country}
                  onChange={(e) => setCustomAddress({ ...customAddress, country: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Telefono
                </label>
                <input
                  type="text"
                  value={customAddress.phone}
                  onChange={(e) => setCustomAddress({ ...customAddress, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={customAddress.email}
                  onChange={(e) => setCustomAddress({ ...customAddress, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* ================================================================ */}
      {/* RESUMEN */}
      {/* ================================================================ */}
      <Section
        title="Resumen"
        icon={Calculator}
        isOpen={sections.summary}
        onToggle={() => toggleSection('summary')}
      >
        <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4 space-y-2">
          {/* IVA groups */}
          {Object.entries(totals.byIva).map(([rate, group]) => (
            <div key={rate} className="flex justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400">
                Base imponible ({rate}%):
              </span>
              <span className="text-slate-900 dark:text-white font-medium">{group.base.toFixed(2)} EUR</span>
            </div>
          ))}
          {Object.entries(totals.byIva).map(([rate, group]) => {
            const ivaAmount = group.base * (Number(rate) / 100)
            if (ivaAmount === 0) return null
            return (
              <div key={`iva-${rate}`} className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">IVA {rate}%:</span>
                <span className="text-slate-900 dark:text-white font-medium">{ivaAmount.toFixed(2)} EUR</span>
              </div>
            )
          })}

          {/* Shipping */}
          {!noShipping && totals.shippingPrice > 0 && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Transporte:</span>
                <span className="text-slate-900 dark:text-white font-medium">
                  {totals.shippingPrice.toFixed(2)} EUR
                </span>
              </div>
              {totals.shippingIva > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">
                    IVA transporte {totals.shippingIvaRate}%:
                  </span>
                  <span className="text-slate-900 dark:text-white font-medium">
                    {totals.shippingIva.toFixed(2)} EUR
                  </span>
                </div>
              )}
            </>
          )}

          {/* Separator */}
          <div className="border-t border-slate-200 dark:border-slate-600 my-2"></div>

          {/* Grand total */}
          <div className="flex justify-between text-lg font-bold">
            <span className="text-slate-800 dark:text-slate-200">TOTAL:</span>
            <span className="text-slate-900 dark:text-white">{totals.grandTotal.toFixed(2)} EUR</span>
          </div>

          {/* COD notice */}
          {isCod && (
            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Contrareembolso: {totals.grandTotal.toFixed(2)} EUR
              </p>
            </div>
          )}

          {/* Intracom notice */}
          {isIntracom && (
            <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Operacion intracomunitaria: IVA al 0%
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* ================================================================ */}
      {/* ACTION BUTTONS */}
      {/* ================================================================ */}
      <div className="flex items-center justify-end gap-3 pb-8">
        <button
          type="button"
          onClick={resetForm}
          className="px-5 py-2.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={createMutation.isPending}
          className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {createMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Crear Factura y Envio
        </button>
      </div>
    </div>
  )
}
